import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, NgZone } from '@angular/core';
import { App, AppState } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { ConnectionStatus, Network } from '@capacitor/network';
import { BehaviorSubject, Observable, combineLatest, map, distinctUntilChanged } from 'rxjs';
import { environment } from 'src/environments/environment';

@Injectable({ providedIn: 'root' })
export class ConnectivityService {
  private deviceOnlineSubject = new BehaviorSubject<boolean>(true);
  private serverReachableSubject = new BehaviorSubject<boolean>(true);

  private consecutiveFailures = 0;
  private readonly FAILURE_THRESHOLD = 2;
  private heartbeatHandle: ReturnType<typeof setInterval> | null = null;

  private isAppActive = true;
  private readonly RESUME_GRACE_PERIOD_MS = 4000;
  private resumeGraceTimeout: ReturnType<typeof setTimeout> | null = null;
  private inResumeGracePeriod = false;

  online$: Observable<boolean> = combineLatest([
    this.deviceOnlineSubject.asObservable(),
    this.serverReachableSubject.asObservable(),
  ]).pipe(
    map(([deviceOnline, serverReachable]) => deviceOnline && serverReachable),
    distinctUntilChanged(),
  );

  offline$: Observable<boolean> = this.online$.pipe(map(online => !online));

  constructor(
    private ngZone: NgZone,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  init() {
    if (Capacitor.isNativePlatform()) {
      Network.getStatus()
        .then(status => this.deviceOnlineSubject.next(status.connected))
        .catch(() => this.deviceOnlineSubject.next(false));

      Network.addListener('networkStatusChange', (status: ConnectionStatus) => {
        this.ngZone.run(() => this.deviceOnlineSubject.next(status.connected));
      });

      // Track app foreground/background transitions (native only)
      App.addListener('appStateChange', (state: AppState) => {
        this.ngZone.run(() => this.onAppStateChange(state.isActive));
      });
    } else {
      this.deviceOnlineSubject.next(navigator.onLine);
      window.addEventListener('online', () => this.ngZone.run(() => this.deviceOnlineSubject.next(true)));
      window.addEventListener('offline', () => this.ngZone.run(() => this.deviceOnlineSubject.next(false)));

      this.document.addEventListener('visibilitychange', () => {
        this.ngZone.run(() => this.onAppStateChange(this.document.visibilityState === 'visible'));
      });
    }
  }

  private onAppStateChange(isActive: boolean) {
    this.isAppActive = isActive;

    if (!isActive) {
      console.log('[Connectivity] app went to background/hidden, suspending failure tracking');
      return;
    }

    console.log('[Connectivity] app resumed/visible again, resetting failure tracking');
    this.consecutiveFailures = 0;

    if (!this.serverReachableSubject.value) {
      this.serverReachableSubject.next(true);
      this.stopHeartbeat();
    }

    if (Capacitor.isNativePlatform()) {
      Network.getStatus()
        .then(status => this.deviceOnlineSubject.next(status.connected))
        .catch(() => void 0);
    } else {
      this.deviceOnlineSubject.next(navigator.onLine);
    }

    this.startResumeGracePeriod();
  }

  private startResumeGracePeriod() {
    this.inResumeGracePeriod = true;
    if (this.resumeGraceTimeout) {
      clearTimeout(this.resumeGraceTimeout);
    }
    this.resumeGraceTimeout = setTimeout(() => {
      this.inResumeGracePeriod = false;
      this.resumeGraceTimeout = null;
    }, this.RESUME_GRACE_PERIOD_MS);
  }

  reportRequestFailure() {
    if (!this.isAppActive || this.inResumeGracePeriod) {
      console.log('[Connectivity] ignoring failure (app inactive or in resume grace period)');
      return;
    }

    this.consecutiveFailures++;
    console.log('[Connectivity] failure count:', this.consecutiveFailures);
    if (this.consecutiveFailures >= this.FAILURE_THRESHOLD) {
      this.ngZone.run(() => this.serverReachableSubject.next(false));
      this.startHeartbeat();
    }
  }

  reportRequestSuccess() {
    this.consecutiveFailures = 0;
    if (!this.serverReachableSubject.value) {
      this.ngZone.run(() => this.serverReachableSubject.next(true));
      this.stopHeartbeat();
    }
  }

  private startHeartbeat() {
    if (this.heartbeatHandle) return;
    console.log('[Connectivity] heartbeat started');
    this.heartbeatHandle = setInterval(() => {
      fetch(environment.graphqlEndpoint.replace('/graphql', '/healthcheck'), {
        method: 'GET',
        cache: 'no-store',
      })
        .then(res => {
          if (res.ok) this.reportRequestSuccess();
        })
        .catch(() => void 0);
    }, 5000);
  }

  private stopHeartbeat() {
    if (this.heartbeatHandle) {
      clearInterval(this.heartbeatHandle);
      this.heartbeatHandle = null;
      console.log('[Connectivity] heartbeat stopped');
    }
  }
}
